import { db } from '../db/db';
import { AccountingEngine } from './accountingEngine';

export interface DiagnosticSuggestion {
  id: string;
  title: string;
  description: string;
}

export interface DiagnosticIssue {
  id: string;
  type: 'Duplicate' | 'NegativeStock' | 'HighValue' | 'AnomalousDeletion';
  title: string;
  description: string;
  severity: 'Critical' | 'Warning';
  timestamp: string;
  voucherId?: number;
  meta?: any;
}

export const DiagnosticEngine = {
  async runPhase1Diagnostics(companyId: number, toDate: string, mode: 'recent' | 'full' = 'recent'): Promise<DiagnosticIssue[]> {
    const issues: DiagnosticIssue[] = [];
    const now = new Date().toISOString();

    // 1. Duplicate Detection & High Value Transaction Alert
    let vouchersQuery = db.vouchers
      .where('companyId').equals(companyId)
      .reverse();
      
    if (mode === 'recent') {
       vouchersQuery = vouchersQuery.limit(100);
    }
    
    const recentVouchers = await vouchersQuery.toArray();

    const voucherEntries = await db.voucherEntries
      .where('voucherId')
      .anyOf(recentVouchers.map(v => v.id!))
      .toArray();

    // Grouping entries by voucherId
    const entriesByVoucher: Record<number, any[]> = {};
    for (const e of voucherEntries) {
      if (!entriesByVoucher[e.voucherId]) entriesByVoucher[e.voucherId] = [];
      entriesByVoucher[e.voucherId].push(e);
    }

    const signatures = new Map<string, number>();

    for (const v of recentVouchers) {
      const entries = entriesByVoucher[v.id!] || [];
      const totalDebit = entries.filter(e => e.type === 'Dr').reduce((sum, e) => sum + e.amount, 0);

      // --- High Value Check ---
      if (totalDebit > 500000) {
        issues.push({
          id: `hv_${v.id}`,
          type: 'HighValue',
          title: `High Value Voucher Found`,
          description: `Voucher ${v.voucherNumber} (${v.type}) is of amount ₹${totalDebit.toFixed(2)}, which exceeds standard limits.`,
          severity: 'Warning',
          timestamp: now,
          voucherId: v.id,
          meta: { amount: totalDebit, vchNo: v.voucherNumber, date: v.date }
        });
      }

      // --- Duplicate Check ---
      const firstEntryLedger = entries[0]?.ledgerId || 0;
      const signature = `${v.date}_${v.type}_${totalDebit}_${firstEntryLedger}`;
      
      if (signatures.has(signature)) {
        const prevId = signatures.get(signature)!;
        issues.push({
          id: `dup_${v.id}_${prevId}`,
          type: 'Duplicate',
          title: `Potential Duplicate Entry`,
          description: `Voucher ${v.voucherNumber} exactly matches another recent entry on ${v.date} for ₹${totalDebit}.`,
          severity: 'Warning',
          timestamp: now,
          voucherId: v.id,
          meta: { originalId: prevId, duplicateId: v.id, amount: totalDebit, vchNo: v.voucherNumber }
        });
      } else {
        signatures.set(signature, v.id!);
      }
    }

    // 2. Negative Stock Detection
    const stockSummary = await AccountingEngine.generateStockSummary(companyId, toDate);
    for (const item of stockSummary.items) {
      if (item.quantity < 0) {
         issues.push({
            id: `ns_${item.id}`,
            type: 'NegativeStock',
            title: `Negative Stock Balance`,
            description: `Stock Item "${item.name}" has dangerously dipped below zero (Qty: ${item.quantity}).`,
            severity: 'Critical',
            timestamp: now,
            meta: { itemId: item.id, itemName: item.name, quantity: item.quantity }
         });
      }
    }

    // 3. Deleted Activity Monitoring
    let recentDeletesQuery = db.vouchers
       .where('companyId').equals(companyId)
       .filter(v => !!v.deleted)
       .reverse();
       
    if (mode === 'recent') {
       recentDeletesQuery = recentDeletesQuery.limit(50);
    }
    
    const recentDeletes = await recentDeletesQuery.toArray();

    let deleteCountLast24h = 0;
    const yesterday = new Date(Date.now() - 86400000).toISOString();

    // Batch-load all entries for deleted vouchers at once (fix N+1)
    const deleteIds = recentDeletes.map(d => d.id!).filter(Boolean);
    const deletedEntries = deleteIds.length > 0
      ? await db.voucherEntries.where('voucherId').anyOf(deleteIds).toArray()
      : [];
    const deletedEntriesByVoucher = new Map<number, typeof deletedEntries>();
    for (const e of deletedEntries) {
      if (!deletedEntriesByVoucher.has(e.voucherId)) deletedEntriesByVoucher.set(e.voucherId, []);
      deletedEntriesByVoucher.get(e.voucherId)!.push(e);
    }

    for (const d of recentDeletes) {
        if (d.deletedAt && d.deletedAt >= yesterday) {
            deleteCountLast24h++;
        }
        
        const entries = deletedEntriesByVoucher.get(d.id!) || [];
        const drSum = entries.filter(e => e.type === 'Dr').reduce((s, e) => s + e.amount, 0);
        
        if (drSum > 100000) {
           issues.push({
              id: `hd_${d.id}`,
              type: 'AnomalousDeletion',
              title: `High Value Deletion`,
              description: `Voucher ${d.voucherNumber} (₹${drSum.toFixed(2)}) was deleted recently. Needs authorization review.`,
              severity: 'Critical',
              timestamp: now,
              voucherId: d.id,
              meta: { amount: drSum, vchNo: d.voucherNumber }
           });
        }
    }

    if (deleteCountLast24h > 5) {
        issues.push({
            id: `fd_freq`,
            type: 'AnomalousDeletion',
            title: `Frequent Deletions Detected`,
            description: `${deleteCountLast24h} vouchers have been deleted in the past 24 hours.`,
            severity: 'Warning',
            timestamp: now,
            meta: { count: deleteCountLast24h }
        });
    }

    return issues;
  },

  async runPhase2Diagnostics(companyId: number, toDate: string, mode: 'recent' | 'full' = 'recent'): Promise<DiagnosticIssue[]> {
    const issues: DiagnosticIssue[] = [];
    const now = new Date().toISOString();

    // 1. GST / Tax Validation
    let recentSalesQuery = db.vouchers
      .where('companyId').equals(companyId)
      .filter(v => ['Sales', 'Purchase'].includes(v.type) && !v.deleted)
      .reverse();
      
    if (mode === 'recent') {
      recentSalesQuery = recentSalesQuery.limit(50);
    }
    
    const recentSales = await recentSalesQuery.toArray();

    const salesEntries = await db.voucherEntries
      .where('voucherId')
      .anyOf(recentSales.map(v => v.id!))
      .toArray();

    // Prefetch ledgers
    const ledgerIds = new Set(salesEntries.map(e => e.ledgerId));
    const ledgersRaw = await db.ledgers.where('id').anyOf([...ledgerIds]).toArray();
    const ledgers = new Map(ledgersRaw.map(l => [l.id, l]));

    // Find Duties & Taxes group
    const groupsRaw = await db.groups.where('companyId').equals(companyId).toArray();
    const dutiesAndTaxesGroups = new Set(groupsRaw.filter(g => g.name.toLowerCase().includes('duties & taxes')).map(g => g.id));
    const sundryDebtorsCreditorsGroups = new Set(groupsRaw.filter(g => g.name.toLowerCase().includes('sundry debtors') || g.name.toLowerCase().includes('sundry creditors')).map(g => g.id));

    // Resolve hierarchical tree for Duties & Taxes or Debtor/Creditors
    const isUnderGroup = (groupId: number | null, targetGroupIds: Set<number | undefined>): boolean => {
      let currentId = groupId;
      while (currentId) {
        if (targetGroupIds.has(currentId)) return true;
        const parent = groupsRaw.find(g => g.id === currentId);
        if (!parent) break;
        currentId = parent.parentGroupId;
      }
      return false;
    };

    for (const v of recentSales) {
      const entries = salesEntries.filter(e => e.voucherId === v.id);
      
      const hasGstLedger = entries.some(e => {
        const l = ledgers.get(e.ledgerId);
        return l && (l.name.toLowerCase().includes('gst') || isUnderGroup(l.groupId, dutiesAndTaxesGroups));
      });

      if (hasGstLedger) {
        // Find Party
        const partyEntry = entries.find(e => {
          const l = ledgers.get(e.ledgerId);
          return l && isUnderGroup(l.groupId, sundryDebtorsCreditorsGroups);
        });

        if (partyEntry) {
          const party = ledgers.get(partyEntry.ledgerId);
          const totalAmount = entries.filter(e => e.type === partyEntry.type).reduce((s, e) => s + e.amount, 0);

          if (party && !party.gstin && totalAmount > 50000) {
              issues.push({
                id: `gstin_${v.id}_${party.id}`,
                type: 'HighValue', // Using HighValue type mapped to Tax Issue 
                title: `Missing GSTIN on High-Value ${v.type}`,
                description: `Party "${party.name}" is missing a GSTIN but is involved in a high-value transaction (₹${totalAmount.toFixed(2)}) with GST calculations.`,
                severity: 'Critical',
                timestamp: now,
                voucherId: v.id,
              });
          }
        }
      }
    }

    // 2. Receivable / Payable Aging (Simple)
    const ninetyDaysAgoDate = new Date(Date.now() - 90 * 86400000).toISOString().split('T')[0];
    
    // Find all debtor ledgers
    const debtorLedgers = ledgersRaw.filter(l => isUnderGroup(l.groupId, sundryDebtorsCreditorsGroups));
    
    for (const ledger of debtorLedgers) {
      const entries = await db.voucherEntries.where('ledgerId').equals(ledger.id!).toArray();
      if (entries.length === 0) continue;

      let drSum = ledger.balanceType === 'Dr' ? ledger.openingBalance : 0;
      let crSum = ledger.balanceType === 'Cr' ? ledger.openingBalance : 0;
      
      let lastTransactionDate = '1970-01-01';

      for (const e of entries) {
         if (e.type === 'Dr') drSum += e.amount;
         if (e.type === 'Cr') crSum += e.amount;
         
         const v = recentSales.find(v => v.id === e.voucherId); // Very minimal date proxy or full load.
      }

      // To save computing on full vouchers map, let's just use recent queries or approximate
      // Realistically we need the voucher dates. 
      const voucherIds = entries.map(e => e.voucherId);
      const agingVouchers = await db.vouchers.where('id').anyOf(voucherIds).toArray();
      for(const v of agingVouchers) {
         if (v.date > lastTransactionDate) lastTransactionDate = v.date;
      }

      const balance = Math.abs(drSum - crSum);

      if (balance > 100000 && lastTransactionDate < ninetyDaysAgoDate) {
         issues.push({
           id: `aging_${ledger.id}`,
           type: 'HighValue',
           title: `Aging Receivable/Payable (> 90 Days)`,
           description: `Ledger "${ledger.name}" has an outstanding balance of ₹${balance.toFixed(2)} with no activity since ${lastTransactionDate}.`,
           severity: 'Warning',
           timestamp: now
         });
      }
    }

    // 3. Voucher Sequence Check
    // Pick the most common voucher types
    for (const vType of ['Sales', 'Payment', 'Receipt']) {
        const typeVouchers = await db.vouchers
           .where('companyId').equals(companyId)
           .filter(v => v.type === vType && !v.deleted)
           .toArray();
           
        // Parse numbers safely
        const sequences = typeVouchers
           .map(v => ({ vId: v.id, numStr: v.voucherNumber, num: parseInt(v.voucherNumber.replace(/\D/g, ''), 10) }))
           .filter(x => !isNaN(x.num))
           .sort((a,b) => a.num - b.num);

        if (sequences.length > 2) {
           for (let i = 1; i < sequences.length; i++) {
              const diff = sequences[i].num - sequences[i-1].num;
              if (diff === 0) {
                 issues.push({
                   id: `seq_dup_${sequences[i].vId}`,
                   type: 'Duplicate',
                   title: `Duplicate Voucher Number (${vType})`,
                   description: `Voucher Number "${sequences[i].numStr}" appears multiple times in ${vType}.`,
                   severity: 'Critical',
                   timestamp: now,
                   voucherId: sequences[i].vId
                 });
              } else if (diff > 1 && diff < 5) {
                 // Small gaps (maybe deleted or skipped)
                 issues.push({
                   id: `seq_gap_${sequences[i].vId}`,
                   type: 'AnomalousDeletion',
                   title: `Missing Voucher Sequence (${vType})`,
                   description: `Expected voucher number between ${sequences[i-1].numStr} and ${sequences[i].numStr} in ${vType}. Check for missing entries.`,
                   severity: 'Warning',
                   timestamp: now
                 });
              }
           }
        }
    }
    
    return issues;
  },

  async runPhase3Suggestions(companyId: number, toDate: string, mode: 'recent' | 'full' = 'recent'): Promise<DiagnosticSuggestion[]> {
    const suggestions: DiagnosticSuggestion[] = [];
    
    // Fetch recent Payment vouchers
    let recentPaymentsQuery = db.vouchers
      .where('companyId').equals(companyId)
      .filter(v => v.type === 'Payment' && !v.deleted)
      .reverse();
      
    if (mode === 'recent') {
      recentPaymentsQuery = recentPaymentsQuery.limit(100);
    }
    
    const recentPayments = await recentPaymentsQuery.toArray();

    if (recentPayments.length === 0) return suggestions;

    const paymentEntries = await db.voucherEntries
      .where('voucherId')
      .anyOf(recentPayments.map(v => v.id!))
      .toArray();

    const ledgersMap = new Map();
    const rawLedgers = await db.ledgers.where('companyId').equals(companyId).toArray();
    rawLedgers.forEach(l => ledgersMap.set(l.id, l));
    
    const cashLedgers = rawLedgers.filter(l => l.name.toLowerCase().includes('cash'));
    const paymentPatterns = new Map<string, { count: number, amount: number, ledgerName: string }>();

    let totalCashPayments = 0;

    for (const v of recentPayments) {
       const entries = paymentEntries.filter(e => e.voucherId === v.id);
       const drEntries = entries.filter(e => e.type === 'Dr');
       const crEntries = entries.filter(e => e.type === 'Cr');
       
       const isCashPayment = crEntries.some(e => cashLedgers.some(cl => cl.id === e.ledgerId));
       const totalAmount = crEntries.reduce((sum, e) => sum + e.amount, 0);

       if (isCashPayment) {
           totalCashPayments++;
           if (totalAmount > 10000) {
              const hash = 'cash_limit';
              if (!paymentPatterns.has(hash)) {
                  suggestions.push({
                     id: 'sugg_cash_limit',
                     title: 'Optimize Cash Payments',
                     description: `You have cash payments exceeding ₹10,000. Under Section 40A(3), consider using bank transfers to ensure expense admissibility.`,
                  });
                  paymentPatterns.set(hash, { count: 1, amount: 0, ledgerName: '' });
              }
           }
       }

       if (drEntries.length === 1 && crEntries.length === 1) {
           const expenseLedger = ledgersMap.get(drEntries[0].ledgerId);
           if (expenseLedger) {
               const patternKey = `${expenseLedger.id}_${drEntries[0].amount}`;
               const existing = paymentPatterns.get(patternKey);
               if (existing) {
                   existing.count++;
               } else {
                   paymentPatterns.set(patternKey, { count: 1, amount: drEntries[0].amount, ledgerName: expenseLedger.name });
               }
           }
       }
    }

    for (const [key, data] of paymentPatterns.entries()) {
        if (key !== 'cash_limit' && data.count >= 3) {
            suggestions.push({
                id: `sugg_repeat_${key}`,
                title: `Recurring Entry: ${data.ledgerName}`,
                description: `You frequently post payments of ₹${data.amount.toFixed(2)} to ${data.ledgerName}. Consider setting up a standard Voucher Template for faster entry.`,
            });
        }
    }

    if (totalCashPayments > 50) {
        suggestions.push({
            id: 'sugg_cash_heavy',
            title: 'Digitize Transactions',
            description: `High volume of cash transactions detected (${totalCashPayments} recent). Consider digitizing payments for better tracking and security.`
        });
    }

    return suggestions;
  },

  async runAllDiagnostics(companyId: number, toDate: string, mode: 'recent' | 'full' = 'recent'): Promise<{ issues: DiagnosticIssue[], suggestions: DiagnosticSuggestion[] }> {
     const [phase1, phase2, phase3] = await Promise.all([
        this.runPhase1Diagnostics(companyId, toDate, mode),
        this.runPhase2Diagnostics(companyId, toDate, mode),
        this.runPhase3Suggestions(companyId, toDate, mode)
     ]);
     
     const all = [...phase1, ...phase2];
     // Sort: Criticals first
     const sortedIssues = all.sort((a, b) => {
         if (a.severity === 'Critical' && b.severity !== 'Critical') return -1;
         if (b.severity === 'Critical' && a.severity !== 'Critical') return 1;
         return 0;
     });
     
     return { issues: sortedIssues, suggestions: phase3 };
  }
};
